import { ActivatedRoute, Router } from '@angular/router';
import {
  ChangeDetectorRef,
  Component,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ColDef, ColGroupDef } from 'ag-grid-community';
import { NomObjectClass, NomObjectLifecycleStatus } from '../../constants';
import { ViewId, ViewPresetService } from '@services/view-preset.service';

import { AgBooleanCellRendererComponent } from '../../../ag-grid/components/cell-renderers/ag-boolean-cell-renderer/ag-boolean-cell-renderer.component';
import { AgDataObjectCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-data-object-cell-renderer/ag-data-object-cell-renderer.component';
import { AgDateCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-date-cell-renderer/ag-date-cell-renderer.component';
import { AgDateTimeFilterComponent } from '../../../ag-grid/components/ag-date-time-filter/ag-date-time-filter.component';
import { AppState } from 'src/app/reducers';
import { CoreService } from '../../services/core.service';
import { ExportSettingsComponent } from '../../components/panels/export-settings/export-settings.component';
import { GeneralDynamicAgGridComponent } from 'src/app/ag-grid/components/general-dynamic-ag-grid/general-dynamic-ag-grid.component';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { LoggerService } from '@services/logger.service';
import { ObjectEditorService } from '../../services/object-editor.service';
import { Observable } from 'rxjs';
import { SchedulesComponent } from '../../components/panels/schedules/schedules.component';
import { Store } from '@ngrx/store';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@env/environment';
import { redirectToObject } from 'src/app/shared/utils/object-utils';

@Component({
  selector: 'app-export-schedules',
  templateUrl:
    '../../../ag-grid/components/general-dynamic-ag-grid/general-dynamic-ag-grid.component.html',
  styleUrl:
    '../../../ag-grid/components/general-dynamic-ag-grid/general-dynamic-ag-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ExportSchedulesComponent extends GeneralDynamicAgGridComponent {
  protected tableTitle = 'My Schedules'; // Table title
  protected tableIcon: IconProp = ['fas', 'calendar-alt'];
  @ViewChild('scheduleEditor') scheduleEditor: SchedulesComponent;

  public viewId: ViewId = ViewId.ExportSchedules;

  // @TODO WAP-435: Auto-loading of data objects is not yet implemented for in-line forms
  hasMasterDetail = false;

  public components: {
    [p: string]: any;
  } = {
    agDateInput: AgDateTimeFilterComponent,
  };

  protected generalGridClassName: string = 'export-schedules-grid';
  constructor(
    protected store: Store<AppState>,
    protected route: ActivatedRoute,
    protected router: Router,
    protected coreService: CoreService,
    protected editService: ObjectEditorService,
    protected cdr: ChangeDetectorRef,
    protected logger: LoggerService,
    protected viewPresetService: ViewPresetService,
    protected toastService: ToastService,
    protected translateService: TranslateService,
  ) {
    super(
      store,
      route,
      cdr,
      logger,
      router,
      viewPresetService,
      toastService,
      translateService,
    );

    this.tableTitle = this.translateService.instant(
      'export-schedules-grid.title.export-schedules',
    );
  }

  onDetailsContainerReady(): void {
    // Dynamically create the ExportSettingsComponent
    // Clear existing content in the details container
    this.dynamicDetailsContainer.clear();

    // Dynamically create the component
    const componentRef = this.dynamicDetailsContainer.createComponent(
      ExportSettingsComponent,
    );

    // Pass the selected object to the dynamically created component
    componentRef.instance.exportSchedule = this.selectedObject;
    this.editor = componentRef.instance;
  }

  protected fetchAgGridData(request: any): Observable<any> {
    return this.coreService.fetchUserSchedulesAgGrid(request);
  }

  createColumnDefs() {
    let columnDefs: (ColDef | ColGroupDef)[] = [
      {
        headerName: this.translateService.instant(
          'grid.column.schedule-company',
        ),
        initialHide: true,
        marryChildren: true,
        children: [
          {
            headerName: this.translateService.instant('grid.column.name'),
            field: 'company.name',
            filter: 'agTextColumnFilter',
            sortable: true,
            initialHide: true,
          },
          {
            headerName: this.translateService.instant('grid.column.is-enabled'),
            field: 'company.is_enabled',
            minWidth: 100,
            maxWidth: 120,
            filterParams: {
              values: ['true', 'false'],
            },
            cellRenderer: AgBooleanCellRendererComponent,
            cellRendererParams: {
              isActive: 'data.company.is_enabled',
            },
            initialHide: true,
          },
        ],
      },
      {
        headerName: this.translateService.instant(
          'grid.column.schedule-parent',
        ),
        marryChildren: true,
        children: [
          {
            headerName: this.translateService.instant('grid.column.workspace'),
            field: 'workspace.title',
            filter: 'agTextColumnFilter',
            sortable: true,
            minWidth: 200,
            cellRenderer: AgDataObjectCellRendererComponent,
            cellRendererParams: {
              titleKey: 'workspace.title',
              icon: ['fas', 'folder'],
              objectId: 'workspace.id',
              objectClassId: 'workspace.object_class_id',
              lifecycleStatus: 'workspace.lifecycle_status',
              onClick: (data) => {
                if (
                  data.context_object.lifecycle_status_id ===
                    NomObjectLifecycleStatus.ACTIVE &&
                  data.can_view &&
                  data.can_edit
                ) {
                  redirectToObject(data.workspace, this.router, true);
                } else {
                  this.toastService.showInfo(
                    this.translateService.instant(
                      'export-schedules.error.you-do-not-have-permissions-or-object-not-active',
                    ),
                    this.translateService.instant(
                      'export-schedules.error.not-available',
                    ),
                  );
                }
              },
            },
            cellStyle: { overflow: 'visible' },
          },
          {
            ...this.createNomenclatureColumnDef({
              headerNameKey: 'grid.column.schedule-context-object-class',
              fieldKey: 'context_object.class_id',
              fieldValue: 'context_object.class_title',
              nomenclatureName: 'object_class',
              otherFilterParams: {
                defaultToNothingSelected: true,
              },
            }),
            minWidth: 110,
            maxWidth: 150,
          },
          {
            headerName: this.translateService.instant(
              'grid.column.schedule-context-object-title',
            ),
            field: 'context_object.title',
            filter: 'agTextColumnFilter',
            sortable: true,
            minWidth: 240,
            cellRenderer: AgDataObjectCellRendererComponent,
            cellRendererParams: {
              titleKey: 'context_object.title',
              objectId: 'context_object.id',
              objectClassId: 'context_object.class_id',
              objectTypeId: 'context_object.type_id',
              lifecycleStatus: 'context_object.lifecycle_status',
              onClick: (data) => {
                if (
                  data.context_object.lifecycle_status_id ===
                    NomObjectLifecycleStatus.ACTIVE &&
                  data.can_view &&
                  data.can_edit
                ) {
                  const className =
                    data.context_object?.class_id === NomObjectClass.FEED
                      ? 'feed'
                      : 'dashboard';
                  const url = this.router.serializeUrl(
                    this.router.createUrlTree([
                      environment.baseUrlEnvironmentSuffix,
                      'analytics',
                      'workspace',
                      data.workspace.id,
                      className,
                      data.context_object.id,
                    ]),
                  );
                  window.open(url, '_blank');
                } else {
                  this.toastService.showInfo(
                    this.translateService.instant(
                      'export-schedules.error.you-do-not-have-permissions-or-object-not-active',
                    ),
                    this.translateService.instant(
                      'export-schedules.error.not-available',
                    ),
                  );
                }
              },
            },
            cellStyle: { overflow: 'visible' },
          },
          {
            ...this.createNomenclatureColumnDef({
              headerNameKey: 'grid.column.lifecycle-status',
              fieldKey: 'context_object.lifecycle_status_id',
              fieldValue: 'context_object.lifecycle_status_name',
              nomenclatureName: 'object_lifecycle_status',
              otherFilterParams: {
                defaultToNothingSelected: true,
              },
            }),
            minWidth: 110,
            maxWidth: 150,
          },
        ],
      },
      {
        headerName: this.translateService.instant(
          'grid.column.schedule-details',
        ),
        marryChildren: true,
        children: [
          {
            headerName: this.translateService.instant('grid.column.name'),
            field: 'title',
            filter: 'agTextColumnFilter',
            cellRenderer: AgDataObjectCellRendererComponent,
            cellRendererParams: {
              titleKey: 'title',
              objectId: 'id',
              objectClassId: 'object_class_id',
              lifecycleStatus: 'lifecycle_status.id',
              onClick: (data) => {
                if (
                  data?.lifecycle_status.id ===
                    NomObjectLifecycleStatus.ACTIVE &&
                  data.can_view &&
                  data.can_edit
                ) {
                  redirectToObject(data, this.router, true);
                } else {
                  this.toastService.showInfo(
                    this.translateService.instant(
                      'export-schedules.error.you-do-not-have-permissions-or-schedule-not-active',
                    ),
                    this.translateService.instant(
                      'export-schedules.error.not-available',
                    ),
                  );
                }
              },
            },
            cellStyle: { overflow: 'visible' },
          },
          {
            headerName: this.translateService.instant(
              'grid.column.description',
            ),
            field: 'description',
            filter: 'agTextColumnFilter',
            initialHide: true,
          },
          {
            headerName: this.translateService.instant(
              'grid.column.schedule-time-of-day',
            ),
            field: 'schedule_time_of_day_to_send',
            filter: 'agTextColumnFilter',
          },
          {
            headerName: this.translateService.instant(
              'grid.column.recipients-count',
            ),
            field: 'recipients_count',
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
            sortable: true,
            maxWidth: 160,
          },
          {
            ...this.createNomenclatureColumnDef({
              headerNameKey: 'grid.column.schedule-execution-status',
              fieldKey: 'execution_status.id',
              fieldValue: 'execution_status.title',
              nomenclatureName: 'export_schedule_execution_status',
              otherFilterParams: {
                defaultToNothingSelected: true,
              },
            }),
          },
          {
            headerName: this.translateService.instant(
              'grid.column.time-last-triggered',
            ),
            field: 'time_last_trigger.absolute',
            filter: 'agDateColumnFilter',
            sortable: true,
            cellRenderer: AgDateCellRendererComponent,
            filterParams: this.filterParams,
          },
          {
            headerName: this.translateService.instant(
              'grid.column.time-last-sent',
            ),
            field: 'time_last_sent.absolute',
            filter: 'agDateColumnFilter',
            sortable: true,
            cellRenderer: AgDateCellRendererComponent,
            filterParams: this.filterParams,
          },
          {
            headerName: this.translateService.instant(
              'grid.column.time-next-trigger',
            ),
            field: 'time_next_trigger.absolute',
            filter: 'agDateColumnFilter',
            sortable: true,
            cellRenderer: AgDateCellRendererComponent,
            filterParams: this.filterParams,
          },
          {
            ...this.createNomenclatureColumnDef({
              headerNameKey: 'grid.column.lifecycle-status',
              fieldKey: 'lifecycle_status.id',
              fieldValue: 'lifecycle_status.title',
              nomenclatureName: 'object_lifecycle_status',
              otherFilterParams: {
                defaultToNothingSelected: true,
              },
            }),
          },
        ],
      },
      {
        headerName: this.translateService.instant(
          'grid.column-group.audit-trail',
        ),
        marryChildren: true,
        children: [
          {
            headerName: this.translateService.instant('grid.column.owner'),
            field: 'owner',
            filter: 'agTextColumnFilter',
            maxWidth: 280,
            sortable: true,
            cellRenderer: AgDataObjectCellRendererComponent,
            cellRendererParams: {
              titleKey: 'owner.name',
              avatarKey: 'owner.avatar_url',
              objectId: 'owner.id',
              objectClassId: 'owner.object_class_id',
              lifecycleStatus: 'owner.lifecycle_status',
              onClick: (data) => {
                this.router.navigate(['admin', 'home', data.user.id]);
              },
            },
            cellStyle: { overflow: 'visible' },
          },
          {
            headerName: this.translateService.instant(
              'grid.column.time-created',
            ),
            field: 'time_created.absolute',
            filter: 'agDateColumnFilter',
            maxWidth: 180,
            sortable: true,
            cellRenderer: AgDateCellRendererComponent,
            filterParams: this.filterParams,
          },
          {
            headerName: this.translateService.instant(
              'grid.column.time-updated',
            ),
            field: 'time_updated.absolute',
            filter: 'agDateColumnFilter',
            maxWidth: 180,
            sortable: true,
            cellRenderer: AgDateCellRendererComponent,
            filterParams: this.filterParams,
          },
        ],
      },
    ];

    return columnDefs;
  }
}
